import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Printer, QrCode, X } from "lucide-react";
import { qrConfigFor, qrDeepLinkBase } from "@arc/tenant-config";
import { useQrSvg } from "./use-qr-svg.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QR_MAX_FACILITIES, qrLabelLines, qrPayload, type QrFacility } from "./qr-payload.js";

/**
 * Print QR labels (ADR-029) — the legacy `qrCode` dashboard view, as a filter-bar view of the drill-down.
 *
 * Pick up to 10 facilities (the legacy recommends ONE), see the code, print it as a **label** (heading + code +
 * first facility + "+N more") or as an **AR anchor** (one facility only — the code Unity anchors to; the legacy
 * refuses more). Codes are NOT stored, exactly as the legacy warns — the print is the artefact.
 *
 * The payload is the legacy scanner's contract (`qr-payload.ts`); the deep-link form prints THIS app's CANONICAL origin
 * (`qrDeepLinkBase`), so a phone camera lands in the rewrite on the right tenant, not the retired host.
 */
export function QrLabelsView({
  realm,
  facilities,
  pathLabelOf,
}: {
  realm: string | null;
  /** The caller's ACTIVE facilities (the drill-down's own flat list), with the fields the payload needs. */
  facilities: readonly (QrFacility & { path?: string })[];
  pathLabelOf: (id: number) => string;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<QrFacility[]>([]);
  const [query, setQuery] = useState("");
  const [printing, setPrinting] = useState<"label" | "anchor" | null>(null);
  const cfg = qrConfigFor(realm ?? "");
  // The canonical origin when served from an alias host (a path-prefixed legacy alias would print the wrong realm).
  const deepLinkBase = typeof window !== "undefined" ? qrDeepLinkBase(realm ?? "", window.location) : "";
  const payload = useMemo(() => qrPayload(chosen, { withLink: cfg.withLink, deepLinkBase }), [chosen, cfg.withLink, deepLinkBase]);
  const svg = useQrSvg(payload);
  const q = query.trim().toLowerCase();
  const candidates = useMemo(
    () => (q === "" ? [] : facilities.filter((f) => !chosen.some((c) => c.id === f.id) && f.name.toLowerCase().includes(q)).slice(0, 8)),
    [facilities, chosen, q],
  );
  const full = chosen.length >= QR_MAX_FACILITIES;
  const lines = qrLabelLines(chosen);

  // Print: render the sheet (portal, print-only CSS in base.css), let the browser lay it out, then print.
  useEffect(() => {
    if (printing == null) return;
    const id = window.setTimeout(() => {
      window.print();
      setPrinting(null);
    }, 50);
    return () => window.clearTimeout(id);
  }, [printing]);

  return (
    <section aria-labelledby="qr-heading" className="flex flex-col gap-4">
      <div>
        <h2 id="qr-heading" className="flex items-center gap-2 text-base font-semibold">
          <QrCode aria-hidden="true" className="size-4" />
          {t("qrLabels.printQrLabels")}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {t("qrLabels.codeIntro", { max: QR_MAX_FACILITIES })}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="flex flex-col gap-3">
          {/* The chosen facilities, as removable chips with their path — the legacy's `qrcodeFacility` rows. */}
          {chosen.length > 0 ? (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label={t("qrLabels.facilitiesInThisCode")}>
              {chosen.map((f) => (
                <li key={f.id} className="border-border bg-muted/40 flex items-center gap-1.5 rounded-full border py-1 pr-1 pl-3 text-sm">
                  <span className="flex flex-col leading-tight">
                    <span>{f.name}</span>
                    <PathLine text={pathLabelOf(f.id)} />
                  </span>
                  <Button variant="ghost" size="icon-sm" aria-label={t("qrLabels.removeName", { name: f.name })} onPress={() => setChosen((c) => c.filter((x) => x.id !== f.id))} className="size-6">
                    <X aria-hidden="true" className="size-3" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm italic">{t("qrLabels.noFacilityYet")}</p>
          )}
          {/* Add — a searchable list of the caller's own facilities; the cap disables it with a reason. */}
          <div className="flex flex-col gap-2">
            <Input
              aria-label={t("qrLabels.addFacility")}
              placeholder={full ? t("qrLabels.maxFacilitiesReached", { max: QR_MAX_FACILITIES }) : t("qrLabels.typeAFacilityNameToAdd")}
              value={query}
              disabled={full}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 max-w-sm"
            />
            {candidates.length > 0 ? (
              <ul className="border-border m-0 flex max-w-sm list-none flex-col divide-y rounded-lg border p-0" aria-label={t("qrLabels.matchingFacilities")}>
                {candidates.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setChosen((c) => [...c, { id: f.id, parentId: f.parentId, name: f.name }]);
                        setQuery("");
                      }}
                      className="hover:bg-muted flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-1.5 text-left text-sm"
                    >
                      <Plus aria-hidden="true" className="text-muted-foreground size-3.5" />
                      <span className="flex flex-col leading-tight">
                        <span>{f.name}</span>
                        <PathLine text={pathLabelOf(f.id)} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        {/* Preview + the two print kinds. */}
        <div className="flex flex-col items-start gap-3">
          <div
            role="img"
            aria-label={payload === "" ? t("qrLabels.noCodeYet") : t("qrLabels.qrCodeFor", { name: lines.name })}
            className="border-border bg-card flex size-48 items-center justify-center rounded-lg border p-2 [&_svg]:size-full"
            dangerouslySetInnerHTML={{ __html: svg ?? "" }}
          />
          <Button variant="default" size="sm" isDisabled={chosen.length === 0} onPress={() => setPrinting("label")} className="gap-1.5">
            <Printer aria-hidden="true" className="size-3.5" />
            {t("qrLabels.printLabel")}
          </Button>
          <Button variant="outline" size="sm" isDisabled={chosen.length !== 1} onPress={() => setPrinting("anchor")} className="dark:border-border gap-1.5">
            <Printer aria-hidden="true" className="size-3.5" />
            {t("qrLabels.printArAnchor")}
          </Button>
          {chosen.length > 1 ? (
            /* The legacy's `arSingleFacilityMessage`: an AR anchor may hold ONE facility only. */
            <p className="text-muted-foreground text-xs">{t("qrLabels.arAnchorSingleFacility")}</p>
          ) : null}
        </div>
      </div>

      {printing != null && svg != null
        ? createPortal(
            <div className="arc-print-sheet" data-kind={printing}>
              <div className="arc-print-label">
                {/* Label: the tenant's heading (the literal "XR Creator" when unset — the legacy's fallback), the code,
                    the first facility, "+N more". AR anchor: the facility name as the heading, the code larger. */}
                <div className="arc-print-heading">{printing === "anchor" ? lines.name : cfg.labelTitle}</div>
                <div className="arc-print-code" dangerouslySetInnerHTML={{ __html: svg }} />
                {printing === "label" ? (
                  <div className="arc-print-text">
                    <div>{lines.name}</div>
                    {lines.rest > 0 ? <div className="arc-print-more">{t("qrLabels.plusMoreFacilities", { count: lines.rest })}</div> : null}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
}

/** A facility's path under its name — omitted for a root, which has none (an empty line looked broken live). */
function PathLine({ text }: { text: string }) {
  return text === "" ? null : <span className="text-muted-foreground text-[0.625rem]">{text}</span>;
}

